/// <reference lib="dom" />
import { clampMapZoom, mapZoomOffset } from '@pz/scene';
import { mapTileUrl } from './map-styles.js';
import {
  TILE_SIZE,
  clampLat,
  mercator,
  tileGrid,
  unmercator,
  wheelZoomDelta,
  wrapLng,
  type TileView,
} from './tile-map-math.js';

/**
 * A small, framework-free slippy map on a canvas.
 *
 * The studio's "Select Initial Location" modal and the widget's shopper-facing
 * map modal are the same map with different buttons around them, so they are the
 * same code: one implementation of drag, wheel, pinch and the tile grid, with
 * the SAME Mercator arithmetic the print renderer uses. Before this there were
 * two half-maps — a pan overlay in the widget and a lat/lng form in the studio —
 * which agreed on neither the zoom clamp nor what a fractional zoom meant.
 *
 * Raster tiles rather than Mapbox GL: they are the tiles the print file is made
 * of, so what the shopper drags is what gets manufactured. A vector preview over
 * a raster print is how a map ends up looking subtly different on the poster
 * than it did on the product page.
 *
 * A design with a pin gets a location marker, the way any web map shows one: it
 * sits ON its place, so panning and zooming carry it along, and dragging it
 * picks a new place. A search drops it on the result in the middle of the map.
 * The answer is still the ordinary lat/lng/zoom of the design's frame — the
 * frame whose printed pin artwork lands on that place — so preview and print
 * need nothing new. Without a pin the centre of the map is the location.
 */

export interface TileMapOptions {
  /** Public Mapbox token. Without one there are no tiles and the map stays blank. */
  token: string;
  /** Mapbox style the tiles come from, `owner/style-id`. */
  source: string;
  /** The design's frame centre (the saved answer), not the pin's place. */
  lat: number;
  lng: number;
  zoom: number;
  /**
   * The map's frame (`MapConfig.viewport`): `zoom` then frames a square of this
   * many map pixels covering the canvas, as it does on the design, so the dialog
   * shows the ground at the scale it prints however big the dialog is. Zooms
   * passed in and reported back stay in the design's terms. Absent, one CSS
   * pixel is one map pixel at `zoom`.
   */
  viewport?: number;
  /** Where the printed pin points, as fractions of the frame's longest side. */
  pin?: {
    x: number; y: number; label?: string;
    /** Contained pin artwork; dimensions in fractions of the map's longest side. */
    artwork?: { url: string; w: number; h: number };
  };
  /** Called when the answer changes (throttled to one per frame) — not on a plain pan under a pin. */
  onChange?: (view: TileView) => void;
}

export interface TileMap {
  /** Shows a saved answer: the frame centre and zoom `getView` returns. */
  setView(lat: number, lng: number, zoom?: number): void;
  /** Shows a searched place: the pin (or, without one, the centre) lands on it, mid-map. */
  setLocation(lat: number, lng: number, zoom?: number): void;
  setStyle(source: string): void;
  /** Zooms by `delta` levels about the pin when it is in sight, else the centre. */
  zoomBy(delta: number): void;
  /** The answer: the frame centre that prints the chosen place under the pin. */
  getView(): TileView;
  /** The chosen place itself — under the pin, or the centre without one. */
  getLocation(): { lat: number; lng: number };
  /** Re-reads the container's size — call after the modal opens or resizes. */
  resize(): void;
  destroy(): void;
}

interface LatLng {
  lat: number;
  lng: number;
}

/** Decoded tiles, keyed `source/z/x/y`; shared by every map on the page. */
const TILE_CACHE = new Map<string, HTMLImageElement | null>();
const TILE_PENDING = new Map<string, Promise<void>>();

/** Arrow-key steps for a focused pin, in CSS pixels (Shift for the long one). */
const KEY_STEP = 4;
const KEY_STEP_LONG = 20;
/** How close to the edge a keyboard-moved pin may get before the map follows. */
const KEY_MARGIN = 24;

/**
 * Whether this environment can host a map at all.
 *
 * Exported so a caller can fall back to a lat/lng form rather than meeting a
 * thrown error: the same bundle runs in jsdom-less tests and on the server.
 */
export function canCreateTileMap(): boolean {
  return typeof document !== 'undefined' && typeof window !== 'undefined';
}

/** Keeps a drag alive past the element's edge; a pointer already gone throws, and is harmless. */
function capture(element: Element, pointerId: number): void {
  try {
    element.setPointerCapture?.(pointerId);
  } catch { /* The drag still works while the pointer stays over the element. */ }
}

/** A place in world pixels at a zoom — unclamped, unlike `panView`/`zoomView`. */
function world(place: LatLng, zoom: number): { x: number; y: number } {
  const point = mercator(place.lat, place.lng, zoom);
  return { x: point.x * TILE_SIZE, y: point.y * TILE_SIZE };
}

/** World pixels at a zoom back to a place, kept inside the projection. */
function ground(x: number, y: number, zoom: number): LatLng {
  const place = unmercator(x / TILE_SIZE, y / TILE_SIZE, zoom);
  return { lat: clampLat(place.lat), lng: wrapLng(place.lng) };
}

function finite(lat: number, lng: number, fallback: LatLng): LatLng {
  return {
    lat: Number.isFinite(lat) ? clampLat(lat) : fallback.lat,
    lng: Number.isFinite(lng) ? wrapLng(lng) : fallback.lng,
  };
}

export function createTileMap(container: HTMLElement, opts: TileMapOptions): TileMap {
  if (!canCreateTileMap()) {
    throw new Error('createTileMap needs a DOM; check canCreateTileMap() first');
  }

  let source = opts.source;
  let destroyed = false;
  const hasPin = Boolean(opts.pin);

  const canvas = document.createElement('canvas');
  canvas.style.display = 'block';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.touchAction = 'none';
  canvas.style.cursor = 'grab';
  container.appendChild(canvas);
  const ctx = canvas.getContext('2d');

  const marker = hasPin ? document.createElement('button') : undefined;
  const previousPosition = container.style.position;
  const previousOverflow = container.style.overflow;
  if (marker) {
    // The marker is positioned inside the map's own box and clipped by it, so
    // a place panned out of sight takes its pin with it.
    container.style.position = 'relative';
    container.style.overflow = 'hidden';
    marker.type = 'button';
    marker.setAttribute('aria-label', opts.pin?.label ?? 'Move location pin');
    marker.title = opts.pin?.label ?? 'Drag the pin to adjust the location';
    // The tip of the drawn pin is the button's bottom centre; that point sits
    // exactly on the place.
    marker.style.cssText = 'position:absolute;left:0;top:0;z-index:2;width:36px;height:44px;margin:0;padding:0;border:0;background:transparent;transform:translate(-50%,-100%);cursor:grab;touch-action:none;filter:drop-shadow(0 2px 2px rgba(0,0,0,.35))';
    marker.innerHTML = '<svg viewBox="0 0 36 44" width="36" height="44" aria-hidden="true"><path d="M18 43C14 36 3 26 3 17a15 15 0 1 1 30 0c0 9-11 19-15 26Z" fill="#c33246" stroke="white" stroke-width="2"/><circle cx="18" cy="17" r="5" fill="white"/></svg>';
    container.appendChild(marker);
  }

  let width = 0;
  let height = 0;
  let dpr = 1;

  // ── State ─────────────────────────────────────────────────────────────────
  // `zoom` is the design's zoom: the answer's and the dialog's alike (the canvas
  // draws it `offset()` levels deeper to fit the frame into the dialog).
  // `camera` is where the dialog looks. `place` is where the pin stands, and
  // `frame` is the answer: the frame centre that prints `place` under the pin.
  // Without a pin all three are the same point.
  let zoom = clampMapZoom(opts.zoom);
  let frame: LatLng = finite(opts.lat, opts.lng, { lat: 0, lng: 0 });
  let place: LatLng = { ...frame };
  let camera: LatLng = { ...frame };
  /** Where the printed pin points, as fractions of the frame's longest side. */
  let anchor = { x: opts.pin?.x ?? 0, y: opts.pin?.y ?? 0 };
  /**
   * True while nobody has picked a place yet: the saved frame is then the truth
   * and the place is read off it, so reopening a dialog hands back exactly the
   * answer it was given even after the pin's artwork is measured.
   */
  let fromFrame = true;
  /** True once the shopper pans or zooms, so a late pin measurement leaves their view alone. */
  let cameraMoved = false;

  const frameSize = (): number => opts.viewport ?? Math.max(width, height);
  const frameOf = (at: LatLng): LatLng => {
    const p = world(at, zoom);
    return ground(p.x - anchor.x * frameSize(), p.y - anchor.y * frameSize(), zoom);
  };
  const placeOf = (at: LatLng): LatLng => {
    const p = world(at, zoom);
    return ground(p.x + anchor.x * frameSize(), p.y + anchor.y * frameSize(), zoom);
  };
  /** A saved answer: the place is whatever its frame puts under the pin. */
  const takeFrame = (next: LatLng): void => {
    frame = next;
    place = hasPin ? placeOf(next) : { ...next };
    fromFrame = true;
  };
  /** A chosen place: the answer is the frame that prints it under the pin. */
  const takePlace = (next: LatLng): void => {
    place = next;
    frame = hasPin ? frameOf(next) : { ...next };
    fromFrame = false;
  };
  /** The pin's anchor or the frame's size changed: keep whichever side is the truth. */
  const reanchor = (): boolean => {
    if (!hasPin) return false;
    if (fromFrame) {
      place = placeOf(frame);
      if (!cameraMoved) camera = { ...place };
      return false;
    }
    frame = frameOf(place);
    return true;
  };

  /** Levels the canvas draws deeper than the design, so the frame fits the dialog. */
  const offset = (): number => mapZoomOffset(opts.viewport, width, height);
  const shownZoom = (): number => zoom + offset();

  /** Where a place is on the canvas, in CSS pixels; the nearest copy of the wrapped world. */
  const screenOf = (at: LatLng): { x: number; y: number } => {
    const z = shownZoom();
    const c = world(camera, z);
    const p = world(at, z);
    const span = TILE_SIZE * 2 ** z;
    const dx = p.x - c.x;
    return { x: width / 2 + dx - span * Math.round(dx / span), y: height / 2 + p.y - c.y };
  };
  /** The place under a canvas point. */
  const groundAt = (point: { x: number; y: number }): LatLng => {
    const z = shownZoom();
    const c = world(camera, z);
    return ground(c.x + point.x - width / 2, c.y + point.y - height / 2, z);
  };
  const inside = (point: { x: number; y: number }, margin = 0): boolean =>
    point.x >= margin && point.x <= width - margin && point.y >= margin && point.y <= height - margin;

  const positionMarker = (): void => {
    if (!marker) return;
    const point = screenOf(place);
    marker.style.left = `${point.x}px`;
    marker.style.top = `${point.y}px`;
    // Well out of sight it is hidden outright, so it cannot be tabbed to
    // somewhere the shopper cannot see; the container clips the near misses.
    marker.style.visibility = inside(point, -48) ? '' : 'hidden';
  };

  let drawQueued = false;
  let changeQueued = false;

  const raf = (fn: () => void): void => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(fn);
    else setTimeout(fn, 16);
  };

  /**
   * Both the redraw and the change callback are collapsed to one per frame.
   * A drag fires dozens of pointer events per frame and a modal that re-runs its
   * geocoder or re-renders a preview on each one drops to a slideshow.
   */
  const scheduleDraw = (): void => {
    if (drawQueued || destroyed) return;
    drawQueued = true;
    raf(() => {
      drawQueued = false;
      draw();
    });
  };
  const scheduleChange = (): void => {
    if (changeQueued || destroyed || !opts.onChange) return;
    changeQueued = true;
    raf(() => {
      changeQueued = false;
      if (!destroyed) opts.onChange?.({ ...frame, zoom });
    });
  };
  /** The marker moves in the same call as the state, not a frame later. */
  const changed = (answer: boolean): void => {
    positionMarker();
    scheduleDraw();
    if (answer) scheduleChange();
  };

  const measure = (): void => {
    const rect = container.getBoundingClientRect();
    const nextWidth = Math.max(1, Math.round(rect.width || container.clientWidth || 1));
    const nextHeight = Math.max(1, Math.round(rect.height || container.clientHeight || 1));
    const resized = nextWidth !== width || nextHeight !== height;
    width = nextWidth;
    height = nextHeight;
    dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    // Without a viewport the frame is the canvas, so its size is geometry.
    if (resized && !opts.viewport && reanchor()) scheduleChange();
    positionMarker();
  };

  // ── Camera moves ──────────────────────────────────────────────────────────
  /** Drags the map by screen pixels. Under a pin that only looks around. */
  const pan = (dx: number, dy: number): void => {
    const c = world(camera, shownZoom());
    camera = ground(c.x - dx, c.y - dy, shownZoom());
    cameraMoved = true;
    if (!hasPin) takePlace(camera);
    changed(!hasPin);
  };

  /**
   * Zooms by `delta` levels keeping the ground under `point` where it is.
   *
   * The delta is clamped against the DESIGN's zoom first. Clamping only the
   * result — or clamping the canvas zoom, which runs a level or two off the
   * design's — moved the map without zooming it at the ends of the range.
   */
  const zoomAbout = (delta: number, point: { x: number; y: number }): void => {
    const target = clampMapZoom(zoom + delta, zoom);
    if (Math.abs(target - zoom) < 1e-9) return;
    const held = groundAt(point);
    zoom = target;
    const p = world(held, shownZoom());
    camera = ground(p.x - (point.x - width / 2), p.y - (point.y - height / 2), shownZoom());
    cameraMoved = true;
    // The pin keeps its place; the frame that prints it is re-cut at the new zoom.
    takePlace(hasPin ? place : camera);
    changed(true);
  };

  const tileKey = (z: number, x: number, y: number): string => `${source}/${z}/${x}/${y}`;

  /**
   * One tile, from the cache or fetched.
   *
   * Fetched as an `Image` rather than through `fetch`, so the browser's own HTTP
   * cache serves a tile the print preview already asked for, and so a decoded
   * bitmap is what lands in the cache rather than bytes to decode per frame.
   * A tile that fails is remembered as null: a 404 at the edge of the world must
   * not be retried on every frame of a drag.
   */
  const tileImage = (z: number, x: number, y: number): HTMLImageElement | null => {
    const key = tileKey(z, x, y);
    const hit = TILE_CACHE.get(key);
    if (hit !== undefined) return hit;
    if (TILE_PENDING.has(key)) return null;

    const url = mapTileUrl(z, x, y, source, opts.token);
    if (!url) {
      TILE_CACHE.set(key, null);
      return null;
    }
    const image = new Image();
    image.crossOrigin = 'anonymous';
    const job = new Promise<void>((resolve) => {
      image.onload = (): void => {
        TILE_CACHE.set(key, image);
        TILE_PENDING.delete(key);
        resolve();
        scheduleDraw();
      };
      image.onerror = (): void => {
        TILE_CACHE.set(key, null);
        TILE_PENDING.delete(key);
        resolve();
      };
    });
    TILE_PENDING.set(key, job);
    image.src = url;
    return null;
  };

  function draw(): void {
    if (!ctx || destroyed) return;
    if (width === 0 || height === 0) measure();
    positionMarker();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const grid = tileGrid({ ...camera, zoom: shownZoom() }, width, height, dpr);
    for (const tile of grid.tiles) {
      const image = tileImage(tile.z, tile.x, tile.y);
      if (!image) continue;
      // Half a pixel of bleed, for the same reason the print renderer adds it:
      // antialiased edges on both sides of a shared boundary blend with the
      // background instead of each other, drawing a faint grid over the map.
      const bleed = 0.5 / dpr;
      ctx.drawImage(image, tile.dx, tile.dy, tile.size + bleed, tile.size + bleed);
    }
  }

  // ── Dragging the map ──────────────────────────────────────────────────────
  // Pointer events cover mouse, touch and pen in one path; capture keeps the
  // drag alive when the finger leaves the canvas, which on a small modal map is
  // most drags.
  const pointers = new Map<number, { x: number; y: number }>();
  let pinchDistance = 0;

  const localPoint = (e: { clientX: number; clientY: number }): { x: number; y: number } => {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const pinchSpan = (): number => {
    const [a, b] = [...pointers.values()];
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };
  const pinchCentre = (): { x: number; y: number } => {
    const [a, b] = [...pointers.values()];
    if (!a || !b) return { x: width / 2, y: height / 2 };
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  };

  const onPointerDown = (e: PointerEvent): void => {
    capture(canvas, e.pointerId);
    pointers.set(e.pointerId, localPoint(e));
    pinchDistance = pinchSpan();
    canvas.style.cursor = 'grabbing';
  };

  const onPointerMove = (e: PointerEvent): void => {
    const previous = pointers.get(e.pointerId);
    if (!previous) return;
    const point = localPoint(e);
    pointers.set(e.pointerId, point);

    if (pointers.size >= 2) {
      // Two fingers: the span between them IS the zoom. Doubling the distance
      // is one level, which is what every map does and what hands expect.
      const span = pinchSpan();
      if (pinchDistance > 0 && span > 0) {
        const delta = Math.log2(span / pinchDistance);
        if (Math.abs(delta) > 0.001) {
          zoomAbout(delta, pinchCentre());
          pinchDistance = span;
        }
      }
      return;
    }

    const dx = point.x - previous.x;
    const dy = point.y - previous.y;
    if (dx === 0 && dy === 0) return;
    pan(dx, dy);
  };

  const onPointerUp = (e: PointerEvent): void => {
    pointers.delete(e.pointerId);
    pinchDistance = pinchSpan();
    if (pointers.size === 0) canvas.style.cursor = 'grab';
  };

  const onWheel = (e: WheelEvent): void => {
    // The page must not scroll out from under a map being zoomed. Registered
    // non-passive precisely so this can be prevented.
    e.preventDefault();
    const delta = wheelZoomDelta(e.deltaY, e.deltaMode);
    if (delta === 0) return;
    zoomAbout(delta, localPoint(e));
  };

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerUp);
  canvas.addEventListener('wheel', onWheel, { passive: false });

  // ── Dragging the pin ──────────────────────────────────────────────────────
  // The pin's tip follows the pointer (less wherever on the pin it was grabbed)
  // and the place under the tip is the pick, live. It stays inside the map.
  let pinDrag: { id: number; grab: { x: number; y: number }; before: LatLng; wasFromFrame: boolean } | undefined;

  const onMarkerDown = (e: PointerEvent): void => {
    if (e.button !== 0 || pinDrag) return;
    e.preventDefault();
    e.stopPropagation?.();
    if (marker) capture(marker, e.pointerId);
    const tip = screenOf(place);
    const at = localPoint(e);
    pinDrag = {
      id: e.pointerId,
      grab: { x: at.x - tip.x, y: at.y - tip.y },
      before: { ...place },
      wasFromFrame: fromFrame,
    };
    if (marker) marker.style.cursor = 'grabbing';
  };
  const onMarkerMove = (e: PointerEvent): void => {
    if (!pinDrag || pinDrag.id !== e.pointerId) return;
    const at = localPoint(e);
    const tip = {
      x: Math.max(0, Math.min(width, at.x - pinDrag.grab.x)),
      y: Math.max(0, Math.min(height, at.y - pinDrag.grab.y)),
    };
    takePlace(groundAt(tip));
    changed(true);
  };
  const endMarker = (e: PointerEvent): void => {
    if (!pinDrag || pinDrag.id !== e.pointerId) return;
    pinDrag = undefined;
    if (marker) marker.style.cursor = 'grab';
  };
  /** The browser took the gesture away: the pin goes back to where it was. */
  const cancelMarker = (e: PointerEvent): void => {
    if (!pinDrag || pinDrag.id !== e.pointerId) return;
    const { before, wasFromFrame } = pinDrag;
    endMarker(e);
    if (wasFromFrame) {
      place = before;
      frame = frameOf(before);
      fromFrame = true;
    } else {
      takePlace(before);
    }
    changed(true);
  };
  const onMarkerKey = (e: KeyboardEvent): void => {
    const directions: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1],
    };
    const direction = directions[e.key];
    if (!direction) return;
    e.preventDefault();
    const step = e.shiftKey ? KEY_STEP_LONG : KEY_STEP;
    const from = screenOf(place);
    const to = { x: from.x + direction[0] * step, y: from.y + direction[1] * step };
    takePlace(groundAt(to));
    // A pin walked to the edge takes the map with it rather than leaving sight.
    if (!inside(to, KEY_MARGIN)) {
      const c = world(camera, shownZoom());
      camera = ground(c.x + direction[0] * step, c.y + direction[1] * step, shownZoom());
      cameraMoved = true;
    }
    changed(true);
  };
  marker?.addEventListener('pointerdown', onMarkerDown);
  marker?.addEventListener('pointermove', onMarkerMove);
  marker?.addEventListener('pointerup', endMarker);
  marker?.addEventListener('pointercancel', cancelMarker);
  marker?.addEventListener('keydown', onMarkerKey);
  // A wheel over the pin zooms the map, as it would a pixel beside it.
  marker?.addEventListener('wheel', onWheel as EventListener, { passive: false });

  // The modal that hosts the map is usually laid out after this runs, so the
  // first measurement is worthless without watching for the real one.
  let observer: ResizeObserver | undefined;
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(() => {
      measure();
      scheduleDraw();
    });
    observer.observe(container);
  }

  measure();
  takeFrame(frame);
  camera = { ...place };
  draw();

  // Find the tip the artwork actually prints: imported pin PNGs carry
  // transparent padding and are contain-fitted in their box. Scene data is not
  // touched, and a failed or CORS-blocked image keeps the box's bottom centre.
  // This fetch shares the preview's image cache.
  if (opts.pin?.artwork) {
    const pin = opts.pin;
    const artwork = pin.artwork!;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (destroyed) return;
      try {
        const tip = visibleTip(image);
        if (!tip) return;
        const fit = Math.min(artwork.w / image.naturalWidth, artwork.h / image.naturalHeight);
        anchor = {
          x: pin.x + (tip.x - 0.5) * image.naturalWidth * fit,
          y: pin.y - artwork.h / 2 + (tip.y - 0.5) * image.naturalHeight * fit,
        };
        changed(reanchor());
      } catch { /* The box anchor still provides a usable picker. */ }
    };
    image.src = artwork.url;
  }

  const endDrags = (): void => {
    pinDrag = undefined;
    pointers.clear();
    pinchDistance = 0;
    canvas.style.cursor = 'grab';
    if (marker) marker.style.cursor = 'grab';
  };

  return {
    setView(lat, lng, nextZoom) {
      endDrags();
      if (nextZoom !== undefined) zoom = clampMapZoom(nextZoom, zoom);
      takeFrame(finite(lat, lng, frame));
      camera = { ...place };
      cameraMoved = false;
      changed(false);
      // Deliberately no onChange: this is the caller telling the map what to
      // show (a restored answer), and echoing it back invites a loop through
      // whatever state the caller is holding.
    },
    setLocation(lat, lng, nextZoom) {
      endDrags();
      if (nextZoom !== undefined) zoom = clampMapZoom(nextZoom, zoom);
      takePlace(finite(lat, lng, place));
      camera = { ...place };
      cameraMoved = false;
      changed(false);
    },
    setStyle(next) {
      if (next === source) return;
      source = next;
      scheduleDraw();
    },
    zoomBy(delta) {
      const pinPoint = hasPin ? screenOf(place) : undefined;
      zoomAbout(delta, pinPoint && inside(pinPoint) ? pinPoint : { x: width / 2, y: height / 2 });
    },
    getView() {
      return { ...frame, zoom };
    },
    getLocation() {
      return { ...place };
    },
    resize() {
      measure();
      scheduleDraw();
    },
    destroy() {
      destroyed = true;
      observer?.disconnect();
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
      canvas.removeEventListener('wheel', onWheel);
      canvas.remove();
      if (marker) {
        marker.remove();
        container.style.position = previousPosition;
        container.style.overflow = previousOverflow;
      }
    },
  };
}

/**
 * The lowest visible point of a pin picture, as fractions of its size: the
 * middle of its bottom-most opaque row. Pins point down, and imported PNGs
 * carry transparent padding a box's bottom centre would miss by a street.
 */
function visibleTip(image: HTMLImageElement): { x: number; y: number } | undefined {
  const probe = document.createElement('canvas');
  const scale = Math.min(1, 512 / Math.max(image.naturalWidth, image.naturalHeight));
  probe.width = Math.max(1, Math.round(image.naturalWidth * scale));
  probe.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = probe.getContext('2d');
  if (!context) return undefined;
  context.drawImage(image, 0, 0, probe.width, probe.height);
  const pixels = context.getImageData(0, 0, probe.width, probe.height).data;
  for (let y = probe.height - 1; y >= 0; y--) {
    let sum = 0;
    let count = 0;
    for (let x = 0; x < probe.width; x++) {
      if (pixels[(y * probe.width + x) * 4 + 3]! < 32) continue;
      sum += x + 0.5;
      count++;
    }
    if (count) return { x: sum / count / probe.width, y: (y + 0.5) / probe.height };
  }
  return undefined;
}
